import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { NavBar } from 'components/NavBar/NavBar'
import { Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { Input } from 'components/Input/Input'
import { tokens } from 'styling/tokens'
import { IDeliveryProvenance, IMaterialDelivery } from 'types/materialReceipt.interface'
import { patchDelivery, logAudit } from 'features/materialReceipt/materialReceiptSlice'
import { computeYield, deriveState, discrepancyOf, fmt, now, pending, STATES, STATE_META, taskForStation } from './materialReceiptData'
import { useMaterialReceiptStation } from './materialReceiptAccess'
import { MaterialReceiptNoAccess } from './MaterialReceiptShell'
import {
  CalcFormula, CalcLine, FieldGrid, FieldLabel, FieldValue, HiddenStep, Ribbon, RibbonBar, RibbonLabel,
  RibbonNode, RibbonStep, StepBlock, StepHead, StepMeta, StepName,
} from './MaterialReceipt.styled'

const Step = ({ title, prov, fields, hidden, hiddenNote, pendingNote }: {
  title: string
  prov?: IDeliveryProvenance[keyof IDeliveryProvenance]
  fields: [string, string][]
  hidden?: boolean
  hiddenNote?: string
  pendingNote?: string
}) => {
  const done = !!prov
  return (
    <StepBlock>
      <StepHead>
        <StepName $done={done}>{title}</StepName>
        {done ? (
          <StepMeta>{prov?.name} ({prov?.by}) · {prov?.at}</StepMeta>
        ) : (
          <StepMeta style={{ fontStyle: 'italic' }}>{pendingNote || 'Pending'}</StepMeta>
        )}
      </StepHead>
      {hidden ? (
        <HiddenStep>🔒 {hiddenNote || 'Hidden at this station'}</HiddenStep>
      ) : (
        <FieldGrid>
          {fields.map(([k, v]) => (
            <div key={k}>
              <FieldLabel>{k}</FieldLabel>
              <FieldValue>{v}</FieldValue>
            </div>
          ))}
        </FieldGrid>
      )}
    </StepBlock>
  )
}

export const MaterialTicketDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const station = useMaterialReceiptStation()
  const { deliveries, config } = useSelector((s: RootState) => s.materialReceipt)
  const t = deliveries.find((d) => d.id === id) || null

  if (!station) return <MaterialReceiptNoAccess />

  if (!t) {
    return (
      <>
        <NavBar />
        <PageWrapper>
          <H1WithBack title='Delivery' arrow onClick={() => navigate('/material-receipt/deliveries')} />
          <BodyText grey>Delivery not found.</BodyText>
        </PageWrapper>
      </>
    )
  }

  const isManager = station === 'MANAGER'
  const state = deriveState(t)
  const y = computeYield(t, config.basis)
  const d = discrepancyOf(t, config.threshold)
  const stepIndex = STATES.indexOf(state)
  const seePreShip = isManager
  const seeYield = isManager
  const seeDiscPct = isManager

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex center gap='0.75rem' mb='16'>
          <H1WithBack title={t.id} arrow onClick={() => navigate('/material-receipt/deliveries')} />
          <StatusPill status={STATE_META[state].tone}>{STATE_META[state].label}</StatusPill>
          {d?.breach && seeDiscPct && <StatusPill status='danger'>Discrepancy {d.pct > 0 ? '+' : ''}{fmt(d.pct)}%</StatusPill>}
          {d?.breach && !seeDiscPct && <StatusPill status='warning'>Flagged — hold for review</StatusPill>}
          <BodyText grey style={{ margin: 0 }}>{t.plate} · {t.supplier} · {t.source || '—'}</BodyText>
        </Flex>

        <Container style={{ marginBottom: '1rem' }}>
          <ContainerContent>
            <Ribbon>
              {STATES.map((s, i) => (
                <Flex key={s} center style={{ flex: 1 }}>
                  <RibbonStep>
                    <RibbonNode $state={i < stepIndex ? 'done' : i === stepIndex ? 'current' : 'todo'} $color={STATE_META[s].color}>
                      {i < stepIndex ? '✓' : i + 1}
                    </RibbonNode>
                    <RibbonLabel $on={i <= stepIndex}>{STATE_META[s].label}</RibbonLabel>
                  </RibbonStep>
                  {i < STATES.length - 1 && <RibbonBar $done={i < stepIndex} />}
                </Flex>
              ))}
            </Ribbon>
            <OtherText grey style={{ textAlign: 'center', display: 'block', marginTop: '0.75rem' }}>{STATE_META[state].blurb}</OtherText>
          </ContainerContent>
        </Container>

        <Flex gap='1rem' style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: 1.3, minWidth: 0 }}>
            <Container>
              <ContainerHeader><TitleText weight='bold'>Captured data</TitleText></ContainerHeader>
              <ContainerContent>
                <Step
                  title='Pre-shipment (source)'
                  prov={t.prov.preShipment}
                  hidden={!seePreShip}
                  hiddenNote='Baseline grade — set at source, shown to management'
                  fields={[['Pre-shipment grade', t.preGrade != null ? `${fmt(t.preGrade)} g/t` : '—']]}
                />
                <Step
                  title='Arrival (weighbridge · mobile)'
                  prov={t.prov.gross}
                  pendingNote='Awaiting weighbridge (mobile)'
                  fields={[
                    ['Truck plate', t.plate || '—'],
                    ['Driver', t.driver || '—'],
                    ['Waybill', t.waybill || '—'],
                    ['Gross weight', t.gross != null ? `${fmt(t.gross)} t` : '—'],
                  ]}
                />
                <Step
                  title='Tare (weighbridge · mobile)'
                  prov={t.prov.tare}
                  pendingNote='Awaiting tare weigh (mobile)'
                  fields={[['Tare weight', t.tare != null ? `${fmt(t.tare)} t` : '—']]}
                />
                <Step
                  title='Lab assay'
                  prov={t.prov.assay}
                  pendingNote='Awaiting lab assay'
                  fields={[
                    ['Moisture', t.moisture != null ? `${fmt(t.moisture)} %` : '—'],
                    ['Arrival grade', t.grade != null ? `${fmt(t.grade)} g/t` : '—'],
                  ]}
                />
              </ContainerContent>
            </Container>
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {seeYield && (
              <Container style={{ marginBottom: '1rem' }}>
                <ContainerHeader>
                  <TitleText weight='bold'>Computed yield <OtherText grey>({config.basis} basis)</OtherText></TitleText>
                </ContainerHeader>
                <ContainerContent>
                  {y ? (
                    <>
                      <CalcLine>
                        <div><BodyText style={{ margin: 0 }}>Net wet weight</BodyText><CalcFormula>gross − tare</CalcFormula></div>
                        <BodyText weight='bold' style={{ margin: 0 }}>{fmt(y.netWet, 2)} t</BodyText>
                      </CalcLine>
                      <CalcLine>
                        <div><BodyText style={{ margin: 0 }}>Net dry weight</BodyText><CalcFormula>net wet × (1 − {fmt(t.moisture)}%)</CalcFormula></div>
                        <BodyText weight='bold' style={{ margin: 0 }}>{fmt(y.netDry, 2)} t</BodyText>
                      </CalcLine>
                      <CalcLine $highlight>
                        <div><BodyText weight='bold' color={tokens.accent.base} style={{ margin: 0 }}>Expected gold</BodyText><CalcFormula>{config.basis === 'DRY' ? 'net dry × grade' : 'net wet × grade'}</CalcFormula></div>
                        <TitleText weight='bold' color={tokens.accent.base}>{fmt(y.gold)} g</TitleText>
                      </CalcLine>
                    </>
                  ) : (
                    <BodyText grey>Awaiting weight and assay data to compute.</BodyText>
                  )}
                </ContainerContent>
              </Container>
            )}

            <Container>
              <ContainerHeader><TitleText weight='bold'>Actions</TitleText></ContainerHeader>
              <ContainerContent>
                <TicketActions t={t} station={station} isManager={isManager} dispatch={dispatch} threshold={config.threshold} />
              </ContainerContent>
            </Container>
          </div>
        </Flex>
      </PageWrapper>
    </>
  )
}

const TicketActions = ({ t, station, isManager, dispatch, threshold }: {
  t: IMaterialDelivery
  station: 'MANAGER' | 'LAB'
  isManager: boolean
  dispatch: AppDispatch
  threshold: number
}) => {
  const [reopen, setReopen] = useState(false)
  const [reason, setReason] = useState('')
  const [moisture, setMoisture] = useState('')
  const [grade, setGrade] = useState('')

  if (t.state === 'FINALIZED') {
    return (
      <div>
        <BodyText grey style={{ marginBottom: '0.875rem' }}>Locked. A change requires a reopen with a reason.</BodyText>
        {!reopen ? (
          <Button variant='secondary' style={{ width: '100%' }} disabled={!isManager} onClick={() => setReopen(true)}>
            {isManager ? 'Reopen for correction' : 'Reopen — Manager only'}
          </Button>
        ) : (
          <>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder='Reason for reopening' labelText='Reason for reopening' nomarg />
            <Flex gap='0.5rem' mt='16'>
              <Button variant='danger' size='sm' disabled={!reason} onClick={() => {
                dispatch(patchDelivery({ id: t.id, fields: { state: 'OPEN' } }))
                dispatch(logAudit({ ticket: t.id, action: 'Reopen', actor: `You (${station})`, reason, from: 'FINALIZED', to: 'OPEN' }))
                setReopen(false); setReason('')
              }}>Confirm reopen</Button>
              <Button variant='secondary' size='sm' onClick={() => { setReopen(false); setReason('') }}>Cancel</Button>
            </Flex>
          </>
        )}
      </div>
    )
  }

  const task = taskForStation(t, station)
  const need = pending(t)

  if (!task) {
    const waiting = t.gross == null ? 'weighbridge (mobile)' : need.includes('tare') ? 'weighbridge tare (mobile)' : need.length === 0 ? 'a manager to finalize' : need.join(' + ')
    return (
      <Flex column center gap='0.25rem' style={{ padding: '0.5rem 0', textAlign: 'center' }}>
        <div style={{ fontSize: '1.4rem' }}>⏳</div>
        <BodyText weight='bold' style={{ margin: 0 }}>Nothing for you right now</BodyText>
        <OtherText grey>Waiting on {waiting}.</OtherText>
      </Flex>
    )
  }

  if (task.kind === 'finalize') {
    return (
      <div>
        <BodyText grey style={{ marginBottom: '0.875rem' }}>Finalizing locks this ticket. Further changes require a reopen with a reason.</BodyText>
        <Button variant='primary' style={{ width: '100%' }} onClick={() => {
          dispatch(patchDelivery({ id: t.id, fields: { state: 'FINALIZED', prov: { ...t.prov, finalized: { at: now(), by: station, name: `You (${station})` } } } }))
          dispatch(logAudit({ ticket: t.id, action: 'Finalize', actor: `You (${station})`, reason: null, from: 'COMPLETED', to: 'FINALIZED' }))
        }}>Finalize & lock</Button>
      </div>
    )
  }

  // assay entry (Lab / Manager)
  const discPct = grade !== '' && t.preGrade != null ? ((+grade - t.preGrade) / t.preGrade) * 100 : null
  const valid = moisture !== '' && grade !== ''
  return (
    <div>
      <BodyText weight='bold' style={{ marginBottom: '0.875rem' }}>Enter lab assay</BodyText>
      <Input type='number' value={moisture} onChange={(e) => setMoisture(e.target.value)} placeholder='e.g. 13.2' labelText='Moisture %' nomarg />
      <div style={{ marginTop: '0.75rem' }}>
        <Input type='number' value={grade} onChange={(e) => setGrade(e.target.value)} placeholder='e.g. 3.6' labelText='Arrival grade (g/t)' nomarg />
      </div>
      {isManager && discPct != null && (
        <div style={{ marginTop: '0.75rem', fontSize: tokens.font.label, color: Math.abs(discPct) > threshold ? tokens.status.danger.fg : tokens.status.success.fg }}>
          {discPct > 0 ? '+' : ''}{discPct.toFixed(1)}% vs pre-shipment. {Math.abs(discPct) > threshold ? 'Opens a discrepancy case.' : 'Within tolerance.'}
        </div>
      )}
      <Button variant='primary' style={{ width: '100%', marginTop: '1rem' }} disabled={!valid} onClick={() => {
        dispatch(patchDelivery({ id: t.id, fields: { moisture: +moisture, grade: +grade, state: 'OPEN', prov: { ...t.prov, assay: { at: now(), by: station, name: `You (${station})` } } } }))
        dispatch(logAudit({ ticket: t.id, action: 'Enter assay', actor: `You (${station})`, reason: null, from: deriveState(t), to: null }))
        setMoisture(''); setGrade('')
      }}>Save assay</Button>
    </div>
  )
}
